/**
 * The robot copy table (twin slice 1, plan W5 step 6; ideation §3c).
 *
 * One row per thing a robot can report, each with the hotel's words, who
 * acts and how urgent it is. The rows cover the Phantas adapter's full set
 * of issue categories (`tests/fixtures/contracts/phantas-issue-categories
 * .json`, held to the adapter by a drift test and to this table by a
 * bijection test), the reasons a robot is unavailable, three derived
 * conditions -- a bare `robot_error`, a stuck task with no named issue, and
 * a robot waiting to be re-localised -- and the three server alerts that
 * no robot report stands behind (api-server 0.6.42, 2026-10-21): a door
 * that didn't open for it, a door it can't get through, and a robot stuck
 * in one spot during its job. The server decides those; the console words
 * them from the alert row (`ALERT_ROW_RULES`, `select.ts`).
 *
 * WORDS ARE THE HOTEL'S. No codes, no "RMF", no metres -- a test reads
 * every row for a site audience. Ops read the same words (one reading, two
 * audiences); their extra detail is the raw category, carried separately.
 * Anything the table does not know falls back to the robot's own text,
 * quoted, for OPS -- a guess at what an unknown category means would be
 * worse than the adapter's sentence -- and to `NEEDS_A_CHECK` for a site
 * audience, which never reads the raw text (it is often a code).
 *
 * COPY STATUS: pending Akash's copy pass 2 (plan gate B).
 */
import { levelWords } from './words';

/** Who has to do something: a person on site, Nexsoss remotely, or nobody
 *  yet (shown, not asked for). */
export type WhoActs = 'you' | 'nexsoss' | 'none';

/** Order inside "Needs you" (ideation §7): safety first, then blocked
 *  work, then upkeep. Cleaning gaps (the fourth band) are slice 2. */
export type Band = 'safety' | 'blocked' | 'upkeep';

export const BAND_RANK: Readonly<Record<Band, number>> = { safety: 0, blocked: 1, upkeep: 2 };

export type RobotCopyKey =
  | 'doorway_blocked'
  | 'emergency_stop'
  | 'clean_cancel_failed'
  | 'exit_blocked'
  | 'boarding_blocked'
  | 'unreachable'
  | 'nav_stalled'
  | 'dustbag'
  | 'fire_relocalize'
  | 'dock_failed'
  | 'localize_failed'
  | 'localize_fallback'
  | 'lift_release_refused'
  | 'lift_held_robot_out'
  | 'robot_error'
  | 'stuck_task'
  | 'stuck_task_lift_rule'
  | 'long_task'
  | 'not_localized'
  | 'lift_handed_back'
  | 'relocalize_after_lift'
  | 'state_channel_stale'
  | 'edge_offline'
  | 'not_reporting'
  | 'door_open_failed'
  | 'door_passage_stalled'
  | 'robot_stalled'
  | 'unmatched';

/** What a row is keyed on. `issue`/`issue_prefix` are Phantas categories
 *  (the prefix's remainder is the row's argument: a lift, a place, a
 *  station); `reason` is `RobotState.unavailable_reason`; `derived` rows
 *  come from the robot's status or its task; `alert` rows from an open
 *  alert of the server's own rule, with the door it names, if any, as the
 *  argument. */
export type RobotCopyMatch =
  | { kind: 'issue'; category: string }
  | { kind: 'issue_prefix'; prefix: string }
  | { kind: 'reason'; reasons: readonly string[] }
  | { kind: 'derived' }
  | { kind: 'alert'; rule: string };

export interface RobotCopyContext {
  /** The robot's name. */
  robot: string;
  /** The prefix row's argument, the raw category for `unmatched`, or the
   *  door an alert row names (a door rule's own, or the door a stalled
   *  robot waits at). */
  arg: string | null;
  /** The robot's map name (`L9`), when its position is trusted; for a
   *  report about a door, the door's floor on the building map when the
   *  map gives one. */
  level: string | null;
  /**
   * `robot_stalled` at a door (`arg`): the door has opened for the robot
   * during its stall (`StallDoor.opened`, `select.ts`), so what it can't
   * get through is the doorway, not a door that has not opened.
   */
  doorOpened?: boolean;
  /**
   * `robot_stalled` at no door the page can name, while whether it waits
   * at one is not known: the doors' live state has not been read, or a
   * door's last request is an old open of the robot's own that the page
   * can't place it against (`StallDoorReading`, `select.ts`). Its steps
   * then offer no cancel (a cancel keeps the door).
   */
  doorUnknown?: boolean;
}

export interface RobotCopyRow {
  key: RobotCopyKey;
  match: RobotCopyMatch;
  whoActs: WhoActs;
  band: Band;
  /** Placed first in its group, whatever its stamp. */
  rankFirst?: true;
  /** Never shown to a site audience: no card, and toasts say only that
   *  Nexsoss needs to check it. */
  opsOnly?: true;
  /** The site's own procedure comes first (a lost robot, Akash 2026-10-06;
   *  every row the site can clear itself, 2026-10-20): a site reader is
   *  never asked to call up front, so an evidence line that only asks is
   *  not shown, and an overdue alert's line drops its "Please call
   *  Nexsoss." Where the cause may still be Nexsoss's, the last step says
   *  when to call ("If it still can't get out, call Nexsoss."), or that
   *  the call follows the check where nothing on site would show whether
   *  it worked (a failed dock: "Then call Nexsoss"); a row that is wholly
   *  the site's (the dust bag) never asks. */
  siteFixesFirst?: true;
  /** Its card is never merged with another robot's of the same report
   *  (`coalescible`, `select.ts`): its steps send the reader to this
   *  robot's own panel, which a card about two robots does not offer. */
  ownCard?: true;
  /** The card's headline, in the hotel's words. */
  headline: (c: RobotCopyContext) => string;
  /** Numbered steps for the person on site. The e-stop's are dynamic and
   *  come from `estopCard` instead; these are its static fallback. */
  steps: (c: RobotCopyContext) => string[];
}

const onLevel = (level: string | null): string => {
  const words = levelWords(level);
  return words ? ` on ${words}` : '';
};

const arg = (c: RobotCopyContext, fallback: string): string => c.arg ?? fallback;

/** Only for a fault that is never the site's: the steps that come first
 *  can't fix it. A row the site can clear ends with a call that comes
 *  after its own step ("If it still ..., call Nexsoss.", "Then call
 *  Nexsoss: ..."). */
export const CALL_NEXSOSS = 'Call Nexsoss.';

/** Putting a robot back on the map at a QR code point, in the order Akash
 *  gave (D35, 2026-10-21): manual mode first, then its own Localize
 *  screen. The steps used to say "push it" (or "drive it") with no manual
 *  mode, which it cannot be moved without. Back to normal mode last, as
 *  the cards say (card P2): in manual mode it never drives off, even once
 *  its job carries on (D28) -- which is why the last step checks its path
 *  and says to step back, as the lost-robot card's step 6 does: out of
 *  manual mode it may set off at once (a paused job carrying on by
 *  itself, or idle behaviour taking it to its charger). It used to read
 *  "Switch it back to normal mode." alone (review BC-F4, 2026-10-31).
 *  Shared by the lost-robot card, the e-stop card and the robot panel, so
 *  the three can't drift apart. Each step keeps an id for the e-stop
 *  card, which numbers its steps by id. */
export type QrScanStepId = 'manual_mode' | 'to_qr_point' | 'localize' | 'qr_scan' | 'normal_mode';
export const QR_SCAN: ReadonlyArray<{ id: QrScanStepId; text: string }> = [
  { id: 'manual_mode', text: 'Switch it to manual mode on its screen.' },
  { id: 'to_qr_point', text: 'Move it to the QR code point on its floor.' },
  { id: 'localize', text: 'On its screen, open Localize and choose the floor it is on.' },
  { id: 'qr_scan', text: 'Choose QR code scan and wait while it scans.' },
  {
    id: 'normal_mode',
    text: 'Check nothing and nobody is in its way, then switch it back to normal mode. Step back: it may start moving.',
  },
];
export const QR_SCAN_STEPS: readonly string[] = QR_SCAN.map((s) => s.text);

/**
 * Getting a robot out of a lift that went back to guests with it inside
 * or in its doorway, after its red estop button held the car: the
 * manual's C1.7 steps 4-5 and the stuck-in-lift card, in the order the
 * decisions fix (review BC-F1/F6, 2026-10-31). The e-stop has to be
 * released before it can be put in manual mode, and manual mode has to
 * follow at once (D20, D44); only then can it be pushed, and always out
 * onto the landing (D33). Both of the places that give this procedure
 * used to start it impossibly: the robot panel with "Switch it to manual
 * mode" while its button was still pressed, the e-stop card with "push it
 * out at the next floor" before the button was released. "Red estop
 * button" (D32). Shared by the robot panel, the e-stop card and the
 * relocalize-after-lift card so they can't drift apart.
 */
export type OutOfLiftStepId = 'car_open' | 'release_manual' | 'push_out';
export const OUT_OF_LIFT: ReadonlyArray<{ id: OutOfLiftStepId; text: string }> = [
  {
    id: 'car_open',
    text: 'If the car has left with it inside, call the car like a guest. Once the doors are open, hold them and keep guests clear.',
  },
  {
    id: 'release_manual',
    text: 'Check nothing and nobody is in its way. If its red estop button is still pressed, twist it to release it. Then at once switch it to manual mode on its screen.',
  },
  {
    id: 'push_out',
    text: 'Push it out onto the landing, about one robot length clear of the doors, never into the car. Let the doors go only when no part of it is over the sill.',
  },
];

/** Out of the lift, then the QR steps from the QR code point on: it is
 *  already in manual mode by then. */
export const OUT_OF_LIFT_THEN_QR: ReadonlyArray<{ id: OutOfLiftStepId | QrScanStepId; text: string }> = [
  ...OUT_OF_LIFT,
  ...QR_SCAN.filter((s) => s.id !== 'manual_mode'),
];

/** The call that follows the QR steps when they did not work. */
export const STILL_LOST_CALL = 'If it still doesn’t know where it is, call Nexsoss.';

/** The hotel's procedure for a robot that has lost its place (Akash,
 *  2026-10-06; D35's order 2026-10-21): the staff put it back on the map
 *  themselves at a QR code point, and call Nexsoss only if that fails. */
export const LOST_ROBOT_STEPS: readonly string[] = [...QR_SCAN_STEPS, STILL_LOST_CALL];

/** The robot panel's button that ends the pause after an e-stop handed a
 *  lift back with the robot inside (`FireRelocalizeAction`'s
 *  `scanned_it` wording, shown to hotel staff; Akash, 2026-10-12). A step
 *  must name a button that exists, so the panel reads this constant. */
export const SCANNED_IT_BUTTON = 'Scanned it';

/** The lost-robot procedure for a robot whose e-stop handed a lift back
 *  with it inside (`relocalize after lift`): out of the lift first (it
 *  may still be in the car, its button still pressed), then the same QR
 *  steps, then the press that tells the system it is back on the map, so
 *  its job carries on. Only this row: a robot lost for any other reason
 *  has no paused job and no such button. */
export const RELOCALIZE_AFTER_LIFT_STEPS: readonly string[] = [
  ...OUT_OF_LIFT_THEN_QR.map((s) => s.text),
  `Then press ${SCANNED_IT_BUTTON} in its robot panel, so it carries on its job.`,
  STILL_LOST_CALL,
];

/** What a site reader reads for a report they may not see the words of:
 *  an ops-only row on a toast, and a category this table does not know
 *  (the robot's raw text, often a code) on a card and a toast alike. */
export const NEEDS_A_CHECK = 'Needs a check by Nexsoss';

/** The robot panel's routine-check button (internally the dustbag
 *  confirm), named exactly as it reads (`DustbagConfirmAction.tsx`): a
 *  step must name a button that exists. Renamed from "Confirm bag
 *  emptied" (Akash, 2026-10-22). */
export const DUSTBAG_BUTTON = 'Confirm routine check done';

/** How a site reader cancels a robot's job, by the buttons its robot
 *  panel shows: the e-stop card's own route (`estop.ts`). Follows "If it
 *  still can't ..., ". */
export const CANCEL_ITS_JOB =
  'cancel its job: open its robot panel, then Current task, then Cancel Task.';

/** At a door that has not opened for the robot (`door_open_failed`, and
 *  `robot_stalled` at such a door): a person at the door, then the call. */
export const DOOR_SHUT_STEPS: readonly string[] = [
  "Check the door isn't blocked or locked.",
  "Open the door by hand if it's safe.",
  "If it still won't open, call Nexsoss.",
];

/** At a door that has opened for a robot that can't get through it
 *  (`door_passage_stalled`, and `robot_stalled` at such a door): the
 *  doorway, then the call. */
export const DOORWAY_STEPS: readonly string[] = [
  'Clear the doorway so it can get through.',
  "If it still can't get through, call Nexsoss.",
];

/** A robot with a goal it never started (`NAV_STALLED`, fleet adapter
 *  0.7.33): what staff do first, in the api-server alert's own words. */
export const NAV_STALLED_SCREEN_STEP = 'Unlock its screen and dismiss any message on it.';

/** A stalled robot's look at its screen before a cancel: a zone clean
 *  paused there reaches the platform as nothing but a robot getting
 *  nowhere (the engine's `_robot_stall_message`, its clean branch). */
export const STALL_SCREEN_STEP = "If it is cleaning, check its clean wasn't paused on its screen.";

/** Every row, in no particular order: ordering is the selector's job. */
export const ROBOT_COPY: readonly RobotCopyRow[] = [
  {
    key: 'doorway_blocked',
    match: { kind: 'issue_prefix', prefix: 'DOORWAY_BLOCKED: ' },
    whoActs: 'you',
    band: 'safety',
    rankFirst: true,
    siteFixesFirst: true,
    headline: (c) => `Standing in the ${arg(c, 'lift')} doorway, holding the car`,
    steps: (c) => [
      `Keep guests clear of the ${arg(c, 'lift')} doors.`,
      'If it is still in the doorway, call Nexsoss.',
    ],
  },
  {
    key: 'emergency_stop',
    match: { kind: 'issue', category: 'emergency_stop' },
    whoActs: 'you',
    band: 'safety',
    headline: () => 'Stopped: its red stop button is pressed',
    // Static fallback only; the card's steps are `estopCard`'s.
    steps: (c) => estopFallbackSteps(c.level),
  },
  {
    key: 'clean_cancel_failed',
    match: { kind: 'issue', category: 'the clean did not stop when it was cancelled' },
    whoActs: 'you',
    band: 'safety',
    headline: () => 'May still be cleaning after its job was cancelled',
    steps: () => ['Go and check whether it is still cleaning.', CALL_NEXSOSS],
  },
  {
    key: 'exit_blocked',
    match: { kind: 'issue_prefix', prefix: 'EXIT_BLOCKED: ' },
    whoActs: 'you',
    band: 'safety',
    siteFixesFirst: true,
    headline: (c) => `Inside ${arg(c, 'the lift')} and can't get out${onLevel(c.level)}`,
    steps: (c) => [
      `Clear the ${arg(c, 'lift')} landing so it can get out.`,
      "If it still can't get out, call Nexsoss.",
    ],
  },
  {
    // No call step: the worker cancels and re-tries the trip itself (the
    // card clears on each cancel), and after its last try it fails the job
    // and tells Nexsoss with its own Issue (`boarding_retry.py`). That
    // failed job raises no card and no alert for the site: its record is
    // synthetic, which the alert engine skips. The site has nothing to
    // call about.
    key: 'boarding_blocked',
    match: { kind: 'issue_prefix', prefix: 'BOARDING_BLOCKED: ' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: (c) =>
      `Can't get into ${arg(c, 'the lift')}${onLevel(c.level)}: something is in the car`,
    steps: (c) => [
      `Ask guests to wait for the next car, or move the trolley out of ${arg(c, 'the lift')}.`,
    ],
  },
  {
    // The call hangs on the card, not on a retry: the fleet adapter raises
    // "cannot reach" only once it has given up, and cancels the robot's job
    // then, so a cleared way does not bring it back. Only an idle return
    // keeps trying, and its arrival takes the card down. A job's card stays
    // up (30 min), so "still here a few minutes after" means: call.
    key: 'unreachable',
    match: { kind: 'issue_prefix', prefix: 'cannot reach ' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: (c) => `Can't get to ${arg(c, 'where it is going')}`,
    steps: (c) => [
      `Check nothing is blocking the way near ${arg(c, 'where it is going')}.`,
      'If this card is still here a few minutes after you clear the way, call Nexsoss.',
    ],
  },
  {
    // Fleet adapter 0.7.33 / edge-bridge 0.12.6 (sg-nomad-01, 2026-10-22):
    // the robot TOOK its goal and has not moved at all for ~30 s, and the
    // edge's one automatic re-send of the goal (at 15 s) did not start it.
    // Both times it happened the robot started the moment staff unlocked its
    // touchscreen, so the screen comes first; the path second, since a robot
    // boxed in before its first metre looks the same from here. The call
    // last, after the site's own check. One alert: the api-server's "stuck
    // in one spot" stands down while this is open, so this card is the one
    // staff read.
    key: 'nav_stalled',
    match: { kind: 'issue', category: 'NAV_STALLED' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: (c) => `Has a job but isn't moving${onLevel(c.level)}: check its screen`,
    steps: () => [
      NAV_STALLED_SCREEN_STEP,
      'Check its path is clear.',
      "If it still doesn't move, call Nexsoss.",
    ],
  },
  {
    // The routine dustbag TASK: the robot has parked at a station and
    // waits for a person (`empty the dustbag at <station>` is raised only
    // by the fleet adapter's dustbag wait, `RobotCommandHandle.py`). It is
    // a request, not a sensor reading -- nothing on the robot reports a
    // full bag -- so it asks (sg-nomad-01, 2026-10-22) and names the robot
    // from its record rather than saying the bag is full. Named, so its
    // card is never merged with another robot's (`ownCard`).
    key: 'dustbag',
    match: { kind: 'issue_prefix', prefix: 'empty the dustbag at ' },
    whoActs: 'you',
    band: 'upkeep',
    siteFixesFirst: true,
    ownCard: true,
    // The ROUTINE CHECK (renamed from the dustbag task, 2026-10-22): the
    // same sentence the api-server's alert and email carry.
    headline: (c) =>
      `${c.robot || 'The robot'} is at ${arg(c, 'its maintenance point')} for a routine check — empty the dustbag and check the brushes, then confirm.`,
    steps: () => [
      'Empty the dustbag and check the brushes.',
      `Press ${DUSTBAG_BUTTON} in its robot panel.`,
    ],
  },
  {
    key: 'fire_relocalize',
    match: { kind: 'issue', category: 'confirm which floor this robot is on' },
    whoActs: 'you',
    band: 'blocked',
    headline: () => 'After the fire alarm: confirm which floor it is on',
    steps: (c) => fireConfirmSteps(c.level),
  },
  {
    // The site's first (2026-10-20): a blocked charger is the usual cause.
    // The call follows the check, not a retry: the fleet adapter never
    // retries a failed dock, so a cleared charger does not by itself get
    // it charging, and with automatic charging off the panel reads IDLE
    // even on the pile. Only charging ends the report.
    key: 'dock_failed',
    match: { kind: 'issue', category: 'the robot did not dock onto its charger' },
    whoActs: 'you',
    band: 'upkeep',
    siteFixesFirst: true,
    headline: () => "Couldn't plug into its charger",
    steps: () => [
      'Check nothing is blocking its charger.',
      "Then call Nexsoss: it won't try to dock again by itself.",
    ],
  },
  {
    key: 'localize_failed',
    match: { kind: 'issue', category: 'localize_failed' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: () => 'Lost its place on the map',
    steps: () => [...LOST_ROBOT_STEPS],
  },
  {
    key: 'localize_fallback',
    match: { kind: 'issue', category: 'localize_fallback' },
    whoActs: 'nexsoss',
    band: 'blocked',
    opsOnly: true,
    headline: () => "A lift's starting point needs checking",
    steps: () => [],
  },
  {
    key: 'lift_release_refused',
    match: { kind: 'issue', category: 'lift claim not released' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Still holding a lift after being asked to let it go',
    steps: () => [],
  },
  {
    // In the Nexsoss group and ranked FIRST there (plan W5 step 6): the car
    // is held for a robot that is demonstrably not in it, and only a
    // console action (Recover) frees it. Nothing on site helps.
    key: 'lift_held_robot_out',
    match: { kind: 'issue', category: 'lift held with robot out' },
    whoActs: 'nexsoss',
    band: 'blocked',
    rankFirst: true,
    headline: () => "A lift is being held for it, but it isn't in the car",
    steps: () => ['Nothing for you to press.'],
  },
  {
    key: 'robot_error',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Reports an error',
    steps: () => [],
  },
  {
    key: 'stuck_task',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => "Its job isn't moving forward",
    steps: () => [],
  },
  {
    // The same alert with a lift rule in force at the site: most likely the
    // site's own block, so it isn't Nexsoss's to act on. "May": RMF names the
    // destination and not the lift.
    key: 'stuck_task_lift_rule',
    match: { kind: 'derived' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: () => 'Its job may be waiting for a blocked lift',
    steps: () => [
      'It carries on when the block ends, or a supervisor can release it in Devices.',
      "If the job shouldn't wait, cancel it.",
      'If no lift rule explains it, call Nexsoss.',
    ],
  },
  {
    // The server's `task_long_running` (Akash, 2026-10-12): a job in
    // progress past its limit, 30 min unless configured. Nexsoss is told by
    // the alert itself. Upkeep, below anything blocked: a long job is often
    // a big one, not a stuck one.
    key: 'long_task',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'upkeep',
    headline: (c) => `Its job has been running for over ${c.arg ?? '30'} minutes`,
    steps: () => [],
  },
  {
    // Fleet adapter 0.7.19 (Akash, 2026-10-11): its e-stop held a lift for
    // 30 s, so the adapter paused its job and handed the car back to guests.
    // The e-stop card carries the steps (`estopCard`); this row is the card's
    // "Also:" line and the toast's words.
    key: 'lift_handed_back',
    match: { kind: 'issue_prefix', prefix: 'lift handed back after e-stop: ' },
    whoActs: 'you',
    // Below the lost row it comes with (and every blocked report): what to
    // do is that row's steps, or the e-stop card's; this only says why.
    band: 'upkeep',
    siteFixesFirst: true,
    headline: (c) => `${arg(c, 'Its lift')} is being handed back to guests`,
    steps: () => [],
  },
  {
    // The same release made while it was in the car or its doorway: the car
    // may have carried it to another floor, so it is lost until staff put it
    // back on the map (E7). Its job carries on once it has been: staff
    // say so with the panel's Scanned it press (2026-10-12).
    key: 'relocalize_after_lift',
    match: { kind: 'issue', category: 'relocalize after lift' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: () => 'Lost its place on the map',
    steps: () => [...RELOCALIZE_AFTER_LIFT_STEPS],
  },
  {
    key: 'not_localized',
    match: { kind: 'reason', reasons: ['not_localized', 'pose_not_on_graph', 'location_status_absent'] },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: () => 'Lost its place on the map',
    steps: () => [...LOST_ROBOT_STEPS],
  },
  {
    // The one reason whose advice is "don't go": the robot is usually
    // healthy and driving, and only the platform's view of it is gone.
    // Said of the robot's updates, never as "Nexsoss has lost sight of
    // it": over an evidence line reading "Nexsoss may not know", that
    // claimed Nexsoss knew and did not know in one card.
    key: 'state_channel_stale',
    match: { kind: 'reason', reasons: ['state_channel_stale'] },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => "Its updates have stopped. It is probably fine; you don't need to go",
    steps: () => [],
  },
  {
    key: 'edge_offline',
    match: { kind: 'reason', reasons: ['edge_offline'] },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Its site box stopped reporting',
    steps: () => [],
  },
  {
    // The site's first (2026-10-20): a robot switched off or gone flat is
    // the usual cause, and the server raises no alert for this reason, so
    // the call is the last step, for either outcome of the check (a flat
    // robot won't switch on). "A few minutes later": a robot switched back
    // on takes a while to start up and be heard again.
    key: 'not_reporting',
    match: { kind: 'reason', reasons: ['not_reporting'] },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: () => "The site box can't reach the robot (power or Wi-Fi)",
    steps: () => [
      "Go to where it was last seen and check it is switched on and its battery isn't flat.",
      "If it won't switch on, or it is on and this card is still here a few minutes later, call Nexsoss.",
    ],
  },
  {
    // api-server 0.6.42, UAT C-03 (Akash, 2026-10-21). The door adapter
    // sent OPEN and the door still read shut a minute later; the robot that
    // asked waits at it until it opens, however long. Never "cancel its
    // job": a cancel does not let go of the door, and at sg-nomad-01 the
    // trip back to the charger runs through the same door
    // (`alerts_engine._RULE_DOOR_OPEN_FAILED`), so the fix is a person at
    // the door. The call comes last: whoever is there sees whether it
    // opened. Its alert turns critical after 10 min (`select.ts`).
    key: 'door_open_failed',
    match: { kind: 'alert', rule: 'door_open_failed' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: (c) => `${arg(c, 'A door')}${onLevel(c.level)} didn't open for it`,
    steps: () => [...DOOR_SHUT_STEPS],
  },
  {
    // api-server 0.6.42, UAT C-01/F-02: the door has been open for the
    // robot's own request for a minute and it has not asked for it to
    // close behind it -- squeezing back and forth at a partly blocked
    // doorway. Never "cancel its job", as for `door_open_failed`: at
    // sg-nomad-01 the charger and both dust bag stations are behind
    // L4_SLL_Door, so a cancel starts the trip home back through the same
    // door, and RMF asks no close for a cancelled plan, so the robot keeps
    // the door (`alerts_engine._RULE_DOOR_PASSAGE_STALLED`). The doorway
    // first, then the call, as the engine's own words say.
    key: 'door_passage_stalled',
    match: { kind: 'alert', rule: 'door_passage_stalled' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    headline: (c) => `Can't get through ${arg(c, 'a door')}${onLevel(c.level)}`,
    steps: () => [...DOORWAY_STEPS],
  },
  {
    // api-server 0.6.42, UAT F-02: on a job and no progress for a minute
    // (it has not left a 2 m circle, changed floor or changed job). A lift,
    // the dust bag, charging, an e-stop, an error and not reporting are
    // among the server's exclusions, each with a card of its own. "Stuck in
    // one spot", never "stopped" or "not moving", as the engine words it: a
    // robot nosing back and forth at a doorway is stuck however much it
    // moves, and staff who find "a stopped robot" moving trust the next
    // card less. It says only that the robot is getting nowhere, so any
    // other report on its card heads the card (`byUrgency`, `select.ts`).
    //
    // AT A DOOR (the argument; `stallDoor`, `select.ts`): the door its own
    // request says it is stuck at, the first one this page found, kept
    // while the alert is open. NEVER "cancel its job" there (Akash,
    // 2026-10-21): a cancel keeps the door and the trip home runs back
    // through it. A door that has not opened for it takes
    // `door_open_failed`'s steps -- open it by hand if it's safe; if it
    // won't open, call Nexsoss -- and one that has (`doorOpened`) is a door
    // it "can't get through", with `door_passage_stalled`'s: the doorway,
    // then the call. The engine words the stall the same way from
    // api-server 0.6.43, which also raises it at a door only after 90 s,
    // and only while no door alert covers that door, so a robot stuck at a
    // door is normally carded by the door's alert, not by this.
    //
    // ELSEWHERE the steps end in a cancel, as the engine's do, after a look
    // at its screen (`STALL_SCREEN_STEP`): the card can't tell a clean from
    // any other job, so the look is worded for either. While the page
    // can't tell whether it waits at a door (`doorUnknown`), the call takes
    // the cancel's place.
    //
    // ITS CARD IS ITS OWN (`ownCard`): the cancel is made from this
    // robot's panel, and two robots stuck on one floor at the same minute
    // (blocking each other in a corridor) would otherwise share one card
    // that says "cancel its job" and offers no panel.
    key: 'robot_stalled',
    match: { kind: 'alert', rule: 'robot_stalled' },
    whoActs: 'you',
    band: 'blocked',
    siteFixesFirst: true,
    ownCard: true,
    headline: (c) =>
      c.arg
        ? c.doorOpened
          ? `Stuck at ${c.arg}${onLevel(c.level)} and can't get through`
          : `Stuck at ${c.arg}${onLevel(c.level)}, waiting for it to open`
        : `Stuck in one spot${onLevel(c.level)} during its job`,
    steps: (c) =>
      c.arg
        ? [...(c.doorOpened ? DOORWAY_STEPS : DOOR_SHUT_STEPS)]
        : [
            'Check its path is clear.',
            STALL_SCREEN_STEP,
            c.doorUnknown
              ? "If it still can't move on, call Nexsoss."
              : `If it still can't move on, ${CANCEL_ITS_JOB}`,
          ],
  },
  {
    key: 'unmatched',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: (c) => `Reports: "${c.arg ?? ''}"`,
    steps: () => [],
  },
];

const BY_KEY = new Map<RobotCopyKey, RobotCopyRow>(ROBOT_COPY.map((r) => [r.key, r]));

/** The door rules whose card can be a door's own (`DOOR_CARD_COPY`). */
export type DoorCardKey = Extract<RobotCopyKey, 'door_open_failed' | 'door_passage_stalled'>;

/**
 * A door alert's card when no robot can be named for it (`select.ts`,
 * `doorAlertRobot`): the door's live state has not been read, or its last
 * request is not a robot's (a console Open that failed), and this page has
 * named no robot for the alert before. The card is the door's (its title
 * is the door), so its words never say "it" of a robot.
 * Who acts, band and site-first are the robot rows' of the same rule.
 */
export const DOOR_CARD_COPY: Readonly<Record<DoorCardKey, { headline: string; steps: readonly string[] }>> = {
  door_open_failed: {
    headline: "Didn't open when it was asked to",
    steps: [
      "Check the door isn't blocked or locked.",
      "Open the door by hand if it's safe and a robot is waiting at it.",
      "If it still won't open, call Nexsoss.",
    ],
  },
  door_passage_stalled: {
    headline: "Open for a robot that can't get through",
    steps: [
      'Clear the doorway so the robot can get through.',
      "If it still can't get through, call Nexsoss.",
    ],
  },
};

export function robotCopyRow(key: RobotCopyKey): RobotCopyRow {
  const row = BY_KEY.get(key);
  if (!row) throw new Error(`no robot copy row ${key}`);
  return row;
}

export interface RobotCopyMatchResult {
  row: RobotCopyRow;
  /** The prefix row's argument, or the raw category for `unmatched`. */
  arg: string | null;
}

/**
 * The row for one Phantas issue category, verbatim from `issues[]`.
 *
 * Exact categories first, then prefixes (with the remainder as the
 * argument). Matching is exact and case-sensitive, like the adapter's
 * contract with the worker; anything else is `unmatched`, carrying the raw
 * text.
 */
export function robotCopyForCategory(category: string): RobotCopyMatchResult {
  for (const row of ROBOT_COPY) {
    if (row.match.kind === 'issue' && row.match.category === category) return { row, arg: null };
  }
  for (const row of ROBOT_COPY) {
    if (
      row.match.kind === 'issue_prefix' &&
      category.startsWith(row.match.prefix) &&
      category.length > row.match.prefix.length
    ) {
      return { row, arg: category.slice(row.match.prefix.length) };
    }
  }
  return { row: robotCopyRow('unmatched'), arg: category };
}

/** The row for an `unavailable_reason`, or null for a reason the table
 *  does not word (the robot's status then speaks for itself). */
export function robotCopyForReason(reason: string | null | undefined): RobotCopyRow | null {
  if (!reason) return null;
  for (const row of ROBOT_COPY) {
    if (row.match.kind === 'reason' && row.match.reasons.includes(reason)) return row;
  }
  return null;
}

/**
 * The e-stop row's static steps: `estopCard`'s own, for a card that
 * cannot read the robot's job or whether it is in a car (the live card is
 * always `estopCard`'s; this is the table's fallback). The card's guards
 * become conditions the person checks: in or at a lift, and a cancel that
 * does not take, are still Nexsoss's (`inCarNotice`, `CANCEL_STUCK`);
 * anything else the site releases itself. Worded as the card words them
 * (held equal by `attention-robot-copy.test.ts`); not imported, because
 * `estop.ts` already imports this module.
 */
export function estopFallbackSteps(level: string | null): string[] {
  const words = levelWords(level);
  return [
    words ? `Go to ${words}.` : "Find it: its floor isn't known.",
    "If it is in or at a lift, don't cancel its job and don't twist the button. Call Nexsoss.",
    'If it has a job, cancel it: open its robot panel, then Current task, then Cancel Task.',
    "Wait until Tasks shows its job cancelled. If it doesn't, don't twist the button; call Nexsoss.",
    'Check nothing and nobody is in its way. It may drive off as soon as you twist, for example back to its charger, even with no job.',
    'Twist the red estop button to release it.',
  ];
}

/**
 * "Confirm which floor" (plan W5 step 6). Confirm floor and release
 * resumes the task and does NOT relocalise (`FireRelocalizeAction.tsx`),
 * so the step is conditional on the robot really being where the map
 * says, and it warns that the robot will move.
 */
export function fireConfirmSteps(level: string | null): string[] {
  const words = levelWords(level);
  // The QR route (batch B, Akash 2026-10-31), as the robot panel gives
  // it to the hotel: a robot on another floor, or on none, is put back
  // on the map first, and the press waits until Location shows the floor
  // it was scanned on (C1.3 step 5). These steps used to send ops to
  // "call Nexsoss" for the case the hotel's own panel now tells it how
  // to fix (review BC-F15).
  const release = words
    ? `If it is standing on ${words}, check its path is clear, then press Confirm floor and release in its panel. It will start moving to resume its job. If it is on another floor, put it back on the map first.`
    : 'Its floor isn’t known yet, so put it back on the map first.';
  return [
    'It was in a lift when the alarm started: it may be in a car or on another floor.',
    release,
    'To put it back on the map: if its red estop button is pressed, release it, then at once switch it to manual mode on its screen. Move it to the QR code point on the floor it is on. On its screen, open Localize, choose the floor and choose QR code scan. Then switch it back to normal mode.',
    `Once Location in its panel shows the floor it is on, check its path is clear and press Confirm floor and release. ${STILL_LOST_CALL}`,
  ];
}
